import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAllCompanies } from '../../modules/companies/stores/slice';
import { doGetCompanies } from '../../modules/companies/stores/thunk';

import { selectAllOrderStats } from '../../modules/analytics/stores/slice';
import { doGetAllOrderStats } from '../../modules/analytics/stores/thunk';
import { OrderPerformance } from '../Elements/OrderPerformance';
import usePageView from '../../hooks/usePageView';

import { ROLES } from '../../constants';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Calendar, CircleX, Store } from 'lucide-react';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';

import { PerformanceSkeleton } from './PerformanceSkeleton';

import { DatePickerWithRange } from '../ui/date-picker-range';
import { getMonths } from '../../modules/analytics/anaytics.model';
import { selectAllCarriers } from '../../modules/carriers/stores/slice';
import { doGetCarriers } from '../../modules/carriers/stores/thunk';
import { Paywall } from '../ui/paywall';
import { doContactMe, doStartPreview } from '../../modules/premiumFeatures/stores/thunk';

import { Button } from '../ui/button';

const PerformancePage = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  const role = useSelector((state) => state.auth.user.role);

  const orderStats = useSelector((state) => selectAllOrderStats(state));

  const companies = useSelector((state) => selectAllCompanies(state));
  const carriers = useSelector((state) => selectAllCarriers(state));
  const [selectedCompany, setSelectedCompany] = useState(undefined);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState(undefined);
  const [carrierOpen, setCarrierOpen] = useState(false);

  const months = getMonths();
  const [customRange, setCustomRange] = useState(false);
  const [month, setMonth] = useState(months[0].value);
  const [range, setRange] = useState(months[0].range);
  const [selectKey, setSelectKey] = useState(+new Date());

  useEffect(() => {
    if (month === null) {
      return;
    }
    setRange(months.find((m) => m.value === month)?.range);
  }, [month]);

  const { isFetching } = useSelector((state) => state.analytics);

  // Emit page view event
  usePageView(window.location.href);

  useEffect(() => {
    dispatch(doGetCompanies({ page: 1, take: 1000 }));
    if (user.role === ROLES.ADMIN) {
      dispatch(doGetCarriers({ page: 1, take: 1000 }));
    }
  }, []);

  useEffect(() => {
    const query = {};
    if (range !== null) {
      query['startRange'] = range[0].toISOString();
      query['endRange'] = range[1].toISOString();
    }

    if (selectedCompany) {
      query['companyId'] = selectedCompany;
    }
    if (selectedCarrier) {
      query['carrierId'] = selectedCarrier;
    }

    dispatch(
      doGetAllOrderStats({
        ...query,
      }),
    );
  }, [range, selectedCompany, selectedCarrier, user.selectedCompany]);

  const isEmpty = useMemo(() => orderStats?.slas?.length === 0, [orderStats]);

  const paywallDescriptions = [
    "Monitor Sparqle's performance",
    'Unlock insights in the service we provide to your customers',
    'Test it free and see the impact for yourself!',
  ];
  const contactMe = async () => {
    const params = { feature: 'PERFORMANCE_DASHBOARD' };
    try {
      await dispatch(doContactMe(params));
    } catch (e) {
      //
    }
  };

  const startPreview = async () => {
    const params = { feature: 'PERFORMANCE_DASHBOARD' };
    try {
      await dispatch(doStartPreview(params));
      // fetchData(); // todo
    } catch (e) {
      //
    }
  };
  return (
    <>
      {(!user.fullyTracked || orderStats?.demo) && (
        <Paywall
          title="Performance insights"
          descriptions={paywallDescriptions}
          previewable={orderStats?.previewable}
          contactMe={contactMe}
          startPreview={startPreview}
        />
      )}

      <div className="bg-white min-h-screen">
        <div className="flex justify-center">
          <div className="w-full max-w-screen-xl bg-white">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center mb-2">
                <h1 className="text-2xl font-semibold">Performance insights</h1>
                <Badge variant="highlight" className="ml-3 px-2 py-1 text-xs">
                  Beta
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">Let&apos;s see how we&apos;ve been doing lately</p>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center justify-end mb-6">
              {role === ROLES.ADMIN && (
                <>
                  <Select
                    onValueChange={(newValue) => {
                      setSelectedCarrier(newValue);
                    }}
                    value={selectedCarrier}
                    open={carrierOpen}
                    onOpenChange={setCarrierOpen}
                  >
                    <SelectTrigger className="w-[200px]">
                      <Store className="h-4 w-4 z-1000" />
                      <SelectValue placeholder="Select a carrier" />
                    </SelectTrigger>

                    <SelectContent>
                      <Button
                        className="w-full px-2"
                        variant="secondary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCarrier('');
                          setCarrierOpen(false);
                        }}
                      >
                        <CircleX /> Clear carrier
                      </Button>
                      <SelectSeparator />
                      <SelectGroup>
                        {carriers.map((c) => (
                          <SelectItem key={c.name} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <Select
                    onValueChange={(newValue) => setSelectedCompany(newValue)}
                    value={selectedCompany}
                    open={companyOpen}
                    onOpenChange={setCompanyOpen}
                  >
                    <SelectTrigger className="w-[220px]">
                      <Store className="h-4 w-4" />
                      <SelectValue placeholder="Select a company" />
                    </SelectTrigger>
                    <SelectContent>
                      <Button
                        className="w-full px-2"
                        variant="secondary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCompany('');
                          setCompanyOpen(false);
                        }}
                      >
                        <CircleX /> Clear company
                      </Button>
                      <SelectSeparator />
                      <SelectGroup>
                        {companies.map((c) => (
                          <SelectItem key={c.id} value={c.companyId}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </>
              )}
              <Select
                key={selectKey}
                onValueChange={(newValue) => {
                  setCustomRange(false);
                  setMonth(newValue);
                }}
              >
                <SelectTrigger className="w-[180px] flex items-center">
                  <Calendar className="h-4 w-4" />
                  <SelectValue placeholder={customRange ? 'custom range' : 'This month'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {months.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <DatePickerWithRange
                onChange={(d) => {
                  setCustomRange(true);
                  setSelectKey(+new Date());
                  setRange([d.from, d.to]);
                  // setMonth(null);
                }}
                preset={{ from: range[0], to: range[1] }}
              />
            </div>
            <Separator className="mb-6" />

            {isFetching || !orderStats ? (
              <PerformanceSkeleton />
            ) : isEmpty ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-6">
                <div>No data to show for the selected period (yet).</div>
              </div>
            ) : (
              <OrderPerformance
                orderStats={orderStats}
                range={range}
                companyId={companies.find((c) => c.companyId === selectedCompany)?.id}
                carrierId={selectedCarrier}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PerformancePage;
